import { ItemInstance } from '../types/Item.types';
import { ResourceManager } from './ResourceManager';
import { UpgradeManager } from './UpgradeManager';
import { STORAGE_CAPACITY_BY_LEVEL, SURVIVOR_SLOTS_BY_LEVEL } from '../data/buildings';
import { UidFactory } from './UidFactory';

// === GESTIONNAIRE DU CAMP ===
// Données persistantes entre les runs (localStorage).
// Stocke l'équipement du coffre et les survivants.
// Les limites dépendent du niveau des bâtiments (UpgradeManager).

const STORAGE_KEY = 'deadtide_camp';

/** Classe de survivant */
export type SurvivorClass = 'warrior' | 'ranger' | 'medic';

/** Cap de niveau pour les survivants (Sprint 85). */
export const LEVEL_CAP = 5;

/** Un survivant au camp */
export interface Survivor {
  id: string;
  name: string;
  class: SurvivorClass;
  level: number;
  alive: boolean;
}

/** Instance de consommable dans le coffre */
export interface StoredConsumable {
  uid: string;
  definitionId: string;
}

/** Données sérialisées du camp */
interface CampData {
  storage: ItemInstance[];     // coffre d'équipement
  consumables: StoredConsumable[]; // consommables stockés
  survivors: Survivor[];       // survivants recrutés
  nextSurvivorId: number;
  // NOTE : nextItemUid retiré — la génération d'UID d'items passe désormais
  // par UidFactory (source unique partagée avec InventoryManager).
}

const DEFAULT_CAMP: CampData = {
  storage: [],
  consumables: [],
  survivors: [],
  nextSurvivorId: 1,
};

// Noms de pirates générés aléatoirement
const PIRATE_FIRST_NAMES = [
  'Jack', 'Anne', 'Barbe', 'Morgan', 'Calico', 'Edward',
  'Mary', 'Grace', 'Samuel', 'Olivier', 'Hector', 'Isabella',
  'César', 'Élodie', 'François', 'Madeleine', 'Théo', 'Lucie',
];
const PIRATE_LAST_NAMES = [
  'Bones', 'le Rouge', 'la Terreur', 'Noirvent', 'Lamort',
  'Tempête', 'Crâne', 'le Borgne', 'Dague', 'Flibuste',
  'Crochet', 'Naufrage', 'le Maudit', 'Récif', 'Marée',
];

export class CampManager {
  private data: CampData;
  private _resources: ResourceManager;
  private _upgrades: UpgradeManager;

  constructor() {
    this.data = this.load();
    this._resources = new ResourceManager();
    this._upgrades = new UpgradeManager();
  }

  /** Accès au gestionnaire de ressources */
  getResources(): ResourceManager {
    return this._resources;
  }

  /** Accès au gestionnaire d'upgrades */
  getUpgrades(): UpgradeManager {
    return this._upgrades;
  }

  // === COFFRE ===

  getStorage(): ItemInstance[] {
    return [...this.data.storage];
  }

  getStorageCapacity(): number {
    const level = this._upgrades.getBuildingLevel('storage');
    return STORAGE_CAPACITY_BY_LEVEL[level] ?? 20;
  }

  /** Nombre max de survivants selon le niveau du dortoir */
  getSurvivorCapacity(): number {
    const level = this._upgrades.getBuildingLevel('dormitory');
    return SURVIVOR_SLOTS_BY_LEVEL[level] ?? 3;
  }

  /** Déposer un item dans le coffre (retourne false si plein). */
  storeItem(item: ItemInstance): boolean {
    if (this.data.storage.length >= this.getStorageCapacity()) return false;
    // Plus de protection anti-collision nécessaire : UidFactory garantit
    // l'unicité globale des UIDs entre toutes les sources (Inventory/Camp/loot/craft).
    this.data.storage.push({ uid: item.uid, definitionId: item.definitionId });
    this.save();
    return true;
  }

  /** Retirer un item du coffre par UID */
  withdrawItem(uid: string): ItemInstance | null {
    const idx = this.data.storage.findIndex(i => i.uid === uid);
    if (idx === -1) return null;
    const item = this.data.storage.splice(idx, 1)[0];
    this.save();
    return item;
  }

  /** Créer un item et l'ajouter au coffre */
  addItemToStorage(definitionId: string): ItemInstance | null {
    if (this.data.storage.length >= this.getStorageCapacity()) return null;
    const item: ItemInstance = {
      uid: UidFactory.next('camp'),
      definitionId,
    };
    this.data.storage.push(item);
    this.save();
    return item;
  }

  // === CONSOMMABLES ===

  getConsumables(): StoredConsumable[] {
    return [...this.data.consumables];
  }

  /** Stocker un consommable au camp */
  addConsumable(definitionId: string): StoredConsumable {
    const c: StoredConsumable = {
      uid: UidFactory.next('camp_cons'),
      definitionId,
    };
    this.data.consumables.push(c);
    this.save();
    return c;
  }

  /** Retirer un consommable du coffre */
  withdrawConsumable(uid: string): StoredConsumable | null {
    const idx = this.data.consumables.findIndex(c => c.uid === uid);
    if (idx === -1) return null;
    const c = this.data.consumables.splice(idx, 1)[0];
    this.save();
    return c;
  }

  // === SURVIVANTS ===

  getSurvivors(): Survivor[] {
    return [...this.data.survivors];
  }

  getAliveSurvivors(): Survivor[] {
    return this.data.survivors.filter(s => s.alive);
  }

  /** Ajouter un survivant au camp */
  addSurvivor(survivorClass: SurvivorClass, name?: string): Survivor {
    const survivor: Survivor = {
      id: `survivor_${this.data.nextSurvivorId++}`,
      name: name ?? this.generatePirateName(),
      class: survivorClass,
      level: 1,
      alive: true,
    };
    this.data.survivors.push(survivor);
    this.save();
    return survivor;
  }

  /** Marquer un survivant comme mort */
  killSurvivor(id: string): void {
    const s = this.data.survivors.find(s => s.id === id);
    if (s) s.alive = false;
    this.save();
  }

  /**
   * Sprint 85 — Faire monter les survivants d'un niveau (cap = 5).
   * Utilisé par ExpeditionManager après une expédition réussie sans morts.
   * Retourne le diff par survivant pour que l'UI puisse l'afficher.
   */
  levelUpSurvivors(ids: string[]): Array<{ survivorId: string; survivorName: string; oldLevel: number; newLevel: number }> {
    const results: Array<{ survivorId: string; survivorName: string; oldLevel: number; newLevel: number }> = [];
    for (const id of ids) {
      const s = this.data.survivors.find(sv => sv.id === id);
      if (!s || !s.alive) continue;
      const oldLevel = s.level;
      if (oldLevel >= LEVEL_CAP) continue; // déjà au cap
      s.level = Math.min(LEVEL_CAP, oldLevel + 1);
      results.push({ survivorId: s.id, survivorName: s.name, oldLevel, newLevel: s.level });
    }
    if (results.length > 0) this.save();
    return results;
  }

  /** Générer un nom de pirate aléatoire */
  private generatePirateName(): string {
    const first = PIRATE_FIRST_NAMES[Math.floor(Math.random() * PIRATE_FIRST_NAMES.length)];
    const last = PIRATE_LAST_NAMES[Math.floor(Math.random() * PIRATE_LAST_NAMES.length)];
    return `${first} ${last}`;
  }

  // === PERSISTANCE ===

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch {
      // localStorage plein ou indisponible
    }
  }

  private load(): CampData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        const loaded: CampData = {
          storage: Array.isArray(data.storage) ? data.storage : [],
          consumables: Array.isArray(data.consumables) ? data.consumables : [],
          survivors: Array.isArray(data.survivors) ? data.survivors : [],
          nextSurvivorId: data.nextSurvivorId ?? 1,
        };
        // Migration legacy : signaler à UidFactory tous les UIDs présents +
        // l'ancien compteur nextItemUid pour qu'elle reprenne au-delà.
        for (const item of loaded.storage) UidFactory.observe(item.uid);
        for (const c of loaded.consumables) UidFactory.observe(c.uid);
        if (typeof data.nextItemUid === 'number' && data.nextItemUid > 0) {
          UidFactory.observe(`camp_${data.nextItemUid - 1}`);
        }
        return loaded;
      }
    } catch {
      // données corrompues
    }
    return { storage: [], consumables: [], survivors: [], nextSurvivorId: 1 };
  }

  /** Reset complet (debug) */
  resetAll(): void {
    this.data = {
      storage: [],
      consumables: [],
      survivors: [],
      nextSurvivorId: 1,
    };
    this.save();
  }
}
